import {Injectable, Inject} from '@angular/core';
import {Store} from '@ngrx/store';
import {
  VisitPlanAccountActions,
  VisitPlanAccountAPiActions, VisitPlanAccountChannelActions
} from "../state/visit-plan-account-state/visit-plan-account.actions";
import {Actions, ofType} from "@ngrx/effects";
import {map, Observable, take} from "rxjs";
import {visitPlanAccountFeature} from "../state/visit-plan-account-state/visit-plan-account.state";
import {VisitPlanAccountModel} from "../../models/visit-plan-account.model";
import {VisitPlanCellModel} from "../../models/visit-plan-cell.model";
import {VisitPlanCallModel} from "../../models/visit-plan-call.model";
import {RecurrencePatternModel} from "../../models/recurrence-pattern.model";
import {VisitPlanHttpService} from "./visit-plan-http.service";
import {VisitPlanActions, VisitPlanChannelActions} from "../state/visit-plan-state/visit-plan.actions";
import {VisitPlanDayTotalsModel} from "../../models/visit-plan-day-totals.model";
import {VisitPlanAccountAnalysisModel} from "../../models/visit-plan-account-analysis.model";
import {
  VisitPlanTrackerCallRecurringConfigurationOptionsModel
} from "../../models/visit-plan-tracker-call-recurring-configuration-options.model";

@Injectable()
export class VisitPlanAccountFacadeService {

  constructor(@Inject(Store) private store: Store,
              @Inject(VisitPlanHttpService) private visitPlanHttpService: VisitPlanHttpService,
              @Inject(Actions) private actions$: Actions) {
  }

  fetchNext(visitPlanId: string, params?: Object): void {
    this.store.select(visitPlanAccountFeature.selectCursor()).pipe(take(1)).subscribe((cursor) => {
      if (!!cursor) {
        this.store.dispatch(VisitPlanAccountAPiActions.getVisitPlanAccounts({
          params,
          visitPlanId
        }));
      }
    })
  }

  getMany(visitPlanId: string, params?: Object): void {
    this.store.dispatch(VisitPlanAccountAPiActions.getVisitPlanAccounts({visitPlanId, params}));
  }

  getVisitPlanAccountTotals(visitPlanId: string, params?: any): Observable<VisitPlanDayTotalsModel[]> {
    return this.visitPlanHttpService.getVisitPlanAccountsTotals(visitPlanId, params);
  }

  getVisitPlanAccountAnalysis(visitPlanId: string, params?: any) {
    this.store.dispatch(VisitPlanAccountAPiActions.getVisitPlanAccountsAnalysis({
      visitPlanId,
      params
    }));
  }

  selectAll(): Observable<VisitPlanAccountModel[]> {
    return this.store.select(visitPlanAccountFeature.selectVisitPlanAccounts());
  }

  selectIsLoading(): Observable<boolean> {
    return this.store.select(visitPlanAccountFeature.selectIsLoading());
  }

  selectInitialized(): Observable<boolean> {
    return this.store.select(visitPlanAccountFeature.selectInitialized());
  }

  selectVisitPlanAccountAnalysis(): Observable<VisitPlanAccountAnalysisModel[]> {
    return this.store.select(visitPlanAccountFeature.selectVisitPlanAccountAnalysis());
  }

  clearStore(): void {
    this.store.dispatch(VisitPlanAccountActions.clearStore());
  }

  deleteCallsFromCell(visitPlanId: string, cellId: string, callIds: string[], deleteRecurringConfiguration?: boolean): void {
    this.store.dispatch(VisitPlanAccountAPiActions.deleteCallsFromCell({visitPlanId, cellId, callIds, deleteRecurringConfiguration}));
  }

  onDeleteCallsFromCellResult(): Observable<boolean> {
    return this.actions$.pipe(
      ofType(
        VisitPlanAccountAPiActions.deleteCallsFromCellSuccess,
        VisitPlanAccountAPiActions.deleteCallsFromCellFailure
      ),
      map((action) => action.type === VisitPlanAccountAPiActions.deleteCallsFromCellSuccess.type)
    );
  }

  onUpdateCallsTimeWindowResult(): Observable<boolean> {
    return this.actions$.pipe(
      ofType(
        VisitPlanAccountAPiActions.updateCallsTimeWindowSuccess,
        VisitPlanAccountAPiActions.updateCallsTimeWindowFailure
      ),
      map((action) => action.type === VisitPlanAccountAPiActions.updateCallsTimeWindowSuccess.type)
    );
  }

  onUpdateCellTrackerResult(): Observable<boolean> {
    return this.actions$.pipe(
      ofType(
        VisitPlanAccountAPiActions.updateCellTrackerSuccess,
        VisitPlanAccountAPiActions.updateCellTrackerFailure
      ),
      map((action) => action.type === VisitPlanAccountAPiActions.updateCellTrackerSuccess.type)
    );
  }

  createCall(visitPlanId: string, cellId: string, call: VisitPlanCallModel, recurrencePattern?: RecurrencePatternModel): void {
    this.store.dispatch(VisitPlanAccountAPiActions.createCall({visitPlanId, cellId, call, recurrencePattern}));
  }

  updateCellTracker(visitPlanId: string, visitPlanCell: VisitPlanCellModel, callIdConfigurationUpdateOptions?: Record<string, VisitPlanTrackerCallRecurringConfigurationOptionsModel>): void {
    this.store.dispatch(VisitPlanAccountAPiActions.updateCellTracker({visitPlanId, visitPlanCell, callIdConfigurationUpdateOptions}));
  }

  updateCallsTimeWindow(visitPlanId: string, cellId: string, list: {
    callId: string,
    notBefore: number,
    notAfter: number
  }[]): void {
    this.store.dispatch(VisitPlanAccountAPiActions.updateCallsTimeWindow({visitPlanId, cellId, list}));
  }

  assignCallsToResource(visitPlanId: string, visitScheduleId: string, payload: {
    resourceId: string,
    callIds: string[],
    cellId: string,
    accountId: string,
    dateRange: { from: number, to: number }
  }): void {
    this.store.dispatch(VisitPlanAccountAPiActions.assignCallsToResource({
      visitPlanId,
      visitScheduleId,
      resourceId: payload.resourceId,
      callIds: payload.callIds,
      cellId: payload.cellId,
      accountId: payload.accountId,
      dateRange: payload.dateRange
    }));
  }

  submitVisitPlanTracker(visitPlanId: string, includeIds?: string[], excludeIds?: string[]): void {
    this.store.dispatch(VisitPlanAccountAPiActions.submitVisitPlanTracker({visitPlanId, includeIds, excludeIds}));
  }

  getTrackerChanges(visitPlanId: string, params: { size: number, page: number }): Observable<any> {
    return this.visitPlanHttpService.getTrackerChanges(visitPlanId, params);
  }

  clearCellTrackers() {
    this.store.dispatch(VisitPlanAccountActions.clearCellTrackers());
  }

  discardChanges(visitPlanId: string, includeIds?: string[], excludeIds?: string[]): void {
    this.store.dispatch(VisitPlanAccountAPiActions.discardChanges({visitPlanId, includeIds, excludeIds}));
  }


  subscribeToChannels(): void {
    this.store.dispatch(VisitPlanAccountChannelActions.subscribe());
  }

  unsubscribeFromChannels(): void {
    this.store.dispatch(VisitPlanAccountChannelActions.unsubscribe());
  }

  syncVisitPlan(visitPlanId: string, accountIds: string[]) {
    this.store.dispatch(VisitPlanAccountAPiActions.syncVisitPlan({ visitPlanId, accountIds }));
  }

}
